import React from 'react';
import { Upload, MapPin, Clock } from 'lucide-react';

/**
 * DynamicFormRenderer - Renders form fields dynamically based on API skeleton
 * Supports all field types from the business-categories API
 */
const DynamicFormRenderer = ({
  fields,
  formData,
  onChange,
  onImageUpload,
  onLocationPick,
  onHoursEdit,
  imageRefs,
  uploadingImage,
  errors = {},
}) => {
  if (!fields || !Array.isArray(fields)) return null;

  const renderField = (field, index) => {
    const { slug, type, options = {}, children } = field;

    // Skip fields that are already rendered at the top level
    if (slug === 'grouped' || slug === 'tag') {
      return null;
    }

    // Layout container
    if (type === 'layout') {
      return (
        <div
          key={index}
          className={`${options.axis === 'v' ? 'space-y-4' : options.axis === 's' ? 'space-y-6' : 'grid grid-cols-1 sm:grid-cols-2 gap-4'}`}
        >
          {options.label && options.label !== 'General Information' && (
            <div className="mb-2">
              <h3 className="text-slate-300 font-semibold text-sm">{options.label}</h3>
              {options.hint && <p className="text-slate-500 text-xs mt-1">{options.hint}</p>}
            </div>
          )}
          {children && children.map((child, idx) => renderField(child, `${index}-${idx}`))}
        </div>
      );
    }

    // Hidden field
    if (type === 'hidden') {
      return <input key={index} type="hidden" name={slug} value={formData[slug] || ''} />;
    }

    // Skip if disabled
    if (options.enabled === false) return null;

    // Check dependency
    if (options.dependency) {
      const { slug: depSlug, type: depType, value: depValue } = options.dependency;
      const depFieldValue = formData[depSlug];
      
      if (depType === 'notEmpty' && !depFieldValue) return null;
      if (depType === 'eq' && depFieldValue !== depValue) return null;
    }

    const label = options.label || slug;
    const hint = options.hint || '';
    const required = options.required || false;
    const value = formData[slug] || '';

    // Text input and tag field
    if (type === 'text' || type === 'tagfromname' || type === 'tag') {
      return (
        <FormField key={index} label={label} hint={hint} required={required} error={errors[slug]}>
          <div className={type === 'tag' ? 'relative' : ''}>
            {type === 'tag' && <span className="absolute left-4 top-3 text-slate-400 font-semibold">@</span>}
            <input
              type="text"
              name={slug}
              value={value}
              onChange={(e) => {
                let finalValue = e.target.value;
                if (type === 'tag') {
                  finalValue = finalValue.toUpperCase();
                }
                onChange(slug, finalValue);
              }}
              disabled={options.enabled === false}
              className={`w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30 transition disabled:opacity-50 disabled:cursor-not-allowed ${type === 'tag' ? 'pl-8 uppercase' : ''}`}
              placeholder={hint}
            />
          </div>
        </FormField>
      );
    }

    // Email input
    if (type === 'email') {
      return (
        <FormField key={index} label={label} hint={hint} required={required} error={errors[slug]}>
          <input
            type="email"
            name={slug}
            value={value}
            onChange={(e) => onChange(slug, e.target.value)}
            className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30 transition"
            placeholder={hint}
          />
        </FormField>
      );
    }

    // URL input
    if (type === 'url') {
      return (
        <FormField key={index} label={label} hint={hint} required={required} error={errors[slug]}>
          <input
            type="url"
            name={slug}
            value={value}
            onChange={(e) => onChange(slug, e.target.value)}
            className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30 transition"
            placeholder={hint}
          />
        </FormField>
      );
    }

    // Contact number
    if (type === 'contactnumber') {
      return (
        <FormField key={index} label={label} hint={hint} required={required} error={errors[slug]}>
          <input
            type="tel"
            name={slug}
            value={value}
            onChange={(e) => onChange(slug, e.target.value)}
            className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30 transition"
            placeholder={hint || "+977 98XXXXXXXX"}
          />
        </FormField>
      );
    }

    // Textarea
    if (type === 'textarea') {
      return (
        <FormField key={index} label={label} hint={hint} required={required} error={errors[slug]}>
          <textarea
            name={slug}
            value={value}
            onChange={(e) => onChange(slug, e.target.value)}
            className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30 transition resize-none"
            placeholder={hint}
            rows={4}
          />
        </FormField>
      );
    }

    // Checkbox
    if (type === 'checkbox') {
      return (
        <FormField key={index} label={label} hint={hint} required={required} error={errors[slug]}>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              name={slug}
              checked={!!value}
              onChange={(e) => onChange(slug, e.target.checked)}
              className="w-5 h-5 rounded border-slate-700 bg-slate-800/50 text-emerald-500 focus:ring-2 focus:ring-emerald-500/30"
            />
            <span className="text-slate-300 text-sm">{hint || label}</span>
          </label>
        </FormField>
      );
    }

    // File upload
    if (type === 'file') {
      const maxFiles = parseInt(options.maxFiles) || 1;
      const isMultiple = maxFiles > 1;
      const currentFiles = isMultiple ? (Array.isArray(value) ? value : []) : value;

      // Create a unique ref key for this field
      const refKey = `file_${slug}`;
      
      // Initialize ref if it doesn't exist
      if (!imageRefs.current) {
        imageRefs.current = {};
      }
      if (!imageRefs.current[refKey]) {
        imageRefs.current[refKey] = React.createRef();
      }

      const fileInputRef = imageRefs.current[refKey];

      return (
        <FormField key={index} label={label} hint={hint} required={required} error={errors[slug]}>
          <div>
            <input
              type="file"
              ref={fileInputRef}
              onChange={(e) => onImageUpload(e, slug, isMultiple)}
              accept="image/*"
              multiple={isMultiple}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => {
                console.log('Button clicked for:', slug);
                console.log('Ref key:', refKey);
                console.log('Ref exists:', !!fileInputRef);
                console.log('Ref current:', fileInputRef?.current);
                fileInputRef?.current?.click();
              }}
              disabled={uploadingImage}
              className="w-full h-48 bg-slate-800/50 border-2 border-dashed border-slate-600 rounded-xl hover:border-emerald-500 hover:bg-slate-800/70 transition disabled:opacity-50 disabled:cursor-not-allowed flex flex-col items-center justify-center gap-2 group overflow-hidden"
            >
              {!isMultiple && value ? (
                <img src={value} alt={label} className="w-full h-full object-cover" />
              ) : (
                <>
                  <Upload className="w-6 h-6 text-slate-500 group-hover:text-emerald-400 transition" />
                  <span className="text-xs text-slate-400 group-hover:text-emerald-400 transition">
                    {label} {isMultiple && `(${currentFiles.length}/${maxFiles})`}
                  </span>
                </>
              )}
            </button>
            {isMultiple && currentFiles.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mt-2">
                {currentFiles.map((img, idx) => (
                  <img key={idx} src={img} alt={`${label} ${idx + 1}`} className="w-full h-20 object-cover rounded-lg" />
                ))}
              </div>
            )}
          </div>
        </FormField>
      );
    }

    // Location picker
    if (type === 'location') {
      return (
        <FormField key={index} label={label} hint={hint} required={required} error={errors[slug]}>
          <button
            type="button"
            onClick={onLocationPick}
            className="w-full px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold transition flex items-center justify-center gap-2"
          >
            <MapPin className="w-5 h-5" />
            {hint || 'Pick Location on Map'}
          </button>
        </FormField>
      );
    }

    // Open hours
    if (type === 'openhours') {
      return (
        <FormField key={index} label={label} hint={hint} required={required} error={errors[slug]}>
          <button
            type="button"
            onClick={onHoursEdit}
            className="w-full px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold transition flex items-center justify-center gap-2"
          >
            <Clock className="w-5 h-5" />
            {hint || 'Edit Working Hours'}
          </button>
        </FormField>
      );
    }

    // Nested selection (category dropdown) - skip, handled in category section
    if (type === 'nestedselection') {
      return null;
    }

    // Entity type dropdown - skip, handled in category section
    if (type === 'entitytypedropdown') {
      return null;
    }

    // N-character field (like PAN ID)
    if (type === 'ncharacterfield') {
      const maxLength = parseInt(options.length) || 9;
      return (
        <FormField key={index} label={label} hint={hint} required={required} error={errors[slug]}>
          <input
            type="text"
            name={slug}
            value={value}
            onChange={(e) => {
              const val = e.target.value.slice(0, maxLength);
              onChange(slug, val);
            }}
            maxLength={maxLength}
            className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30 transition"
            placeholder={hint}
          />
          <p className="text-slate-500 text-xs mt-1">{value.length}/{maxLength} characters</p>
        </FormField>
      );
    }

    // Tag verification/data
    if (type === 'tagverification' || type === 'tagdata') {
      return (
        <FormField key={index} label={label} hint={hint} required={required} error={errors[slug]}>
          <input
            type="text"
            name={slug}
            value={value}
            onChange={(e) => onChange(slug, e.target.value)}
            className="w-full px-4 py-3 bg-slate-800/50 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/30 transition"
            placeholder={hint}
          />
        </FormField>
      );
    }

    // Default fallback
    return (
      <div key={index} className="text-slate-500 text-sm">
        Unsupported field type: {type}
      </div>
    );
  };

  return (
    <div className="space-y-4">
      {fields.map((field, index) => renderField(field, index))}
    </div>
  );
};

// Helper component for form fields
const FormField = ({ label, hint, required, error, children }) => (
  <div>
    {label && (
      <label className="block text-slate-300 font-medium mb-2 text-sm">
        {label}
        {required && <span className="text-red-400 ml-1">*</span>}
        {!required && <span className="text-slate-500 ml-1">(optional)</span>}
      </label>
    )}
    {hint && !label && <p className="text-slate-500 text-xs mb-2">{hint}</p>}
    {children}
    {error && <p className="text-red-400 text-xs mt-2">{error}</p>}
  </div>
);

export default DynamicFormRenderer;
